// Stage camera framing for the Character Studio turntable.
//
// The stage used to sit a fixed camera at `[2.6, 1.7, 3.4]` aimed at
// `[0, 0.9, 0]`, which framed about 2.5 m: fine for a human-height character,
// but a taller one lost his head off the top of the frame and a small one was
// a speck. This module is the pure half of the fix: given the bounds of the
// character as measured on the stage, where should the camera go so the whole
// character, or a named part of it, is in frame for the current field of view
// and aspect ratio?
//
// Kept free of three.js and React so the maths is unit-testable on its own
// (tests/unit/characterStudioStageCamera.test.ts).

export type StageVec3 = [number, number, number];

/** Axis-aligned bounds of the displayed character, in stage (world) space. */
export interface StageBounds {
  min: StageVec3;
  max: StageVec3;
}

/** Where to put the camera and what to orbit around. */
export interface StageCameraFit {
  /** Orbit target: the centre of the bounds. */
  target: StageVec3;
  /** Camera position: `target + normalise(direction) * distance`. */
  position: StageVec3;
  /** Distance from `target` to `position`. */
  distance: number;
}

export interface FitStageCameraOptions {
  /** Vertical field of view, in degrees. */
  fov: number;
  /** Viewport width divided by height. */
  aspect: number;
  /**
   * Direction from the target towards the camera. Need not be normalised.
   * Defaults to `DEFAULT_STAGE_DIRECTION`, the studio's original
   * three-quarter angle.
   */
  direction?: StageVec3;
  /**
   * Extra room around the character, as a fraction of its projected extent
   * (0.12 leaves 12% slack). Defaults to `DEFAULT_STAGE_MARGIN`.
   */
  margin?: number;
  /**
   * Also fit the footprint swept through a full turn about the vertical stage
   * axis, so turning the turntable never pushes anything out of frame.
   * Defaults to true. View presets turn it off: they stop the turntable.
   */
  sweep?: boolean;
}

/** The studio's field of view, unchanged from the original fixed camera. */
export const STAGE_FOV = 42;

/** Extra room around a fitted character. */
export const DEFAULT_STAGE_MARGIN = 0.12;

/** The original fixed camera, used until a character has been measured. */
export const LEGACY_STAGE_POSITION: StageVec3 = [2.6, 1.7, 3.4];
export const LEGACY_STAGE_TARGET: StageVec3 = [0, 0.9, 0];

/** The original camera's viewing angle (position minus target). */
export const DEFAULT_STAGE_DIRECTION: StageVec3 = [
  LEGACY_STAGE_POSITION[0] - LEGACY_STAGE_TARGET[0],
  LEGACY_STAGE_POSITION[1] - LEGACY_STAGE_TARGET[1],
  LEGACY_STAGE_POSITION[2] - LEGACY_STAGE_TARGET[2],
];

/** Closest any part of the character may come to the camera plane. */
const NEAR_CLEARANCE = 0.05;

function dot(a: StageVec3, b: StageVec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: StageVec3, b: StageVec3): StageVec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function normalise(v: StageVec3): StageVec3 | null {
  const length = Math.hypot(v[0], v[1], v[2]);
  if (!Number.isFinite(length) || length < 1e-9) return null;
  return [v[0] / length, v[1] / length, v[2] / length];
}

function isFiniteVec3(v: unknown): v is StageVec3 {
  return Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number' && Number.isFinite(n));
}

/** True when `bounds` is finite, non-inverted and not a single point. */
export function isUsableStageBounds(bounds: StageBounds | null | undefined): bounds is StageBounds {
  if (!bounds) return false;
  const values = [...bounds.min, ...bounds.max];
  if (!values.every((n) => Number.isFinite(n))) return false;
  const size = [bounds.max[0] - bounds.min[0], bounds.max[1] - bounds.min[1], bounds.max[2] - bounds.min[2]];
  if (size.some((n) => n < 0)) return false;
  return size.some((n) => n > 1e-6);
}

/** Centre of `bounds`. */
export function stageBoundsCentre(bounds: StageBounds): StageVec3 {
  return [
    (bounds.min[0] + bounds.max[0]) / 2,
    (bounds.min[1] + bounds.max[1]) / 2,
    (bounds.min[2] + bounds.max[2]) / 2,
  ];
}

/**
 * Frame `bounds` from `direction` so the whole character fits.
 *
 * Every corner of the box is tested, together with (unless `sweep` is false)
 * the corners of the square that encloses the character's footprint swept
 * through a full turn about the vertical stage axis, so turning the turntable
 * on after fitting never pushes an arm out of frame. For each point the
 * distance is the smallest at which it sits inside both the vertical and the
 * horizontal half-angle once the margin is applied; the result is the largest
 * of those.
 */
export function fitStageCamera(bounds: StageBounds, options: FitStageCameraOptions): StageCameraFit {
  const target = stageBoundsCentre(bounds);
  const forward = normalise(options.direction ?? DEFAULT_STAGE_DIRECTION) ?? normalise(DEFAULT_STAGE_DIRECTION)!;
  const margin = Math.max(0, options.margin ?? DEFAULT_STAGE_MARGIN);
  const fov = Math.min(Math.max(options.fov, 1), 179);
  const aspect = options.aspect > 0 && Number.isFinite(options.aspect) ? options.aspect : 1;
  const tanV = Math.tan((fov * Math.PI) / 360);
  const tanH = tanV * aspect;

  // Camera basis. Looking straight up or down leaves world-up parallel to
  // the view, so fall back to world X for "right" there.
  const right = normalise(cross([0, 1, 0], forward)) ?? ([1, 0, 0] as StageVec3);
  const up = cross(forward, right);

  const { min, max } = bounds;
  const points: StageVec3[] = [];
  const sweep = options.sweep === false
    ? 0
    : Math.hypot(Math.max(Math.abs(min[0]), Math.abs(max[0])), Math.max(Math.abs(min[2]), Math.abs(max[2])));
  for (const y of [min[1], max[1]]) {
    for (const x of [min[0], max[0]]) for (const z of [min[2], max[2]]) points.push([x, y, z]);
    if (sweep > 0) for (const x of [-sweep, sweep]) for (const z of [-sweep, sweep]) points.push([x, y, z]);
  }

  let distance = 0;
  for (const point of points) {
    const p: StageVec3 = [point[0] - target[0], point[1] - target[1], point[2] - target[2]];
    const along = dot(p, forward);
    const needV = along + (Math.abs(dot(p, up)) * (1 + margin)) / tanV;
    const needH = along + (Math.abs(dot(p, right)) * (1 + margin)) / tanH;
    distance = Math.max(distance, needV, needH, along + NEAR_CLEARANCE);
  }

  return {
    target,
    position: [target[0] + forward[0] * distance, target[1] + forward[1] * distance, target[2] + forward[2] * distance],
    distance,
  };
}

/**
 * Orbit zoom limits for a fitted distance. The original fixed limits (0.6 to
 * 8 m) are kept as the floor and ceiling a human-height character already
 * had, widened only when the character needs it: a tall one can still zoom
 * out well past its fitted framing, a small one can still get close.
 */
export function stageDistanceLimits(distance: number): { minDistance: number; maxDistance: number } {
  const d = Number.isFinite(distance) && distance > 0 ? distance : 4.35;
  return { minDistance: Math.min(0.6, d * 0.25), maxDistance: Math.max(8, d * 1.8) };
}

// ── View presets ────────────────────────────────────────────────────────────

/**
 * A named camera framing, expressed relative to the character's measured
 * bounds rather than in metres, so one preset frames a 0.5 m creature and a
 * 2.5 m one alike. The character is assumed to face +Z, as the stage's own
 * default three-quarter view does.
 */
export interface StageViewPreset {
  /** Stable id, e.g. `'front'`. A `views` entry with a built-in's id replaces it. */
  id: string;
  /** Button text. */
  label: string;
  /** Tooltip. */
  title?: string;
  /** Direction from the framed region towards the camera. Need not be normalised. */
  direction: StageVec3;
  /**
   * Part of the character to frame, as fractions of its bounds per axis:
   * `[0, 0, 0]` is the bounds' minimum corner and `[1, 1, 1]` the maximum.
   * Omit to frame the whole character.
   */
  region?: { min: StageVec3; max: StageVec3 };
  /** Extra room around the region. Defaults to `DEFAULT_STAGE_MARGIN`. */
  margin?: number;
}

/**
 * How a host customises the view presets: an array is merged onto
 * `DEFAULT_STAGE_VIEWS` by id (a matching id replaces the built-in in place,
 * a new id is appended); a function receives the defaults and returns the
 * complete list, so it can also reorder or remove.
 */
export type StageViewsOption =
  | readonly StageViewPreset[]
  | ((defaults: readonly StageViewPreset[]) => readonly StageViewPreset[]);

/**
 * The studio's built-in framings. Close-up regions are proportions of a
 * standing humanoid (head in the top sixth, hands around hip height, feet in
 * the bottom seventh); a differently built character can replace any of them
 * through the `views` prop.
 */
export const DEFAULT_STAGE_VIEWS: readonly StageViewPreset[] = Object.freeze([
  { id: 'front', label: 'Front', title: 'Whole character, from the front', direction: [0, 0.15, 1] },
  { id: 'three-quarter', label: '3/4', title: 'Whole character, three-quarter view', direction: DEFAULT_STAGE_DIRECTION },
  { id: 'profile', label: 'Profile', title: 'Whole character, from the side', direction: [1, 0.15, 0] },
  { id: 'back', label: 'Back', title: 'Whole character, from behind', direction: [0, 0.15, -1] },
  {
    id: 'head',
    label: 'Head',
    title: 'Close on the head and neck',
    direction: [0.25, 0.1, 1],
    region: { min: [0.2, 0.83, 0], max: [0.8, 1, 1] },
    margin: 0.2,
  },
  {
    id: 'hands',
    label: 'Hands',
    title: 'Both hands, around hip height',
    direction: [0.3, 0.2, 1],
    region: { min: [0, 0.3, 0], max: [1, 0.6, 1] },
  },
  {
    id: 'feet',
    label: 'Feet',
    title: 'Close on the feet, from slightly above',
    direction: [0.3, 0.45, 1],
    region: { min: [0.1, 0, 0], max: [0.9, 0.14, 1] },
  },
] satisfies StageViewPreset[]);

/** True when `view` has the fields a preset needs, with finite numbers. */
export function isValidStageView(view: unknown): view is StageViewPreset {
  if (!view || typeof view !== 'object') return false;
  const v = view as Partial<StageViewPreset>;
  if (typeof v.id !== 'string' || !v.id || typeof v.label !== 'string' || !v.label) return false;
  if (!isFiniteVec3(v.direction) || !normalise(v.direction)) return false;
  if (v.region !== undefined && !(isFiniteVec3(v.region?.min) && isFiniteVec3(v.region?.max))) return false;
  if (v.margin !== undefined && !(typeof v.margin === 'number' && Number.isFinite(v.margin))) return false;
  return true;
}

/**
 * The view presets a studio should offer for a `views` option (see
 * `StageViewsOption`). Invalid entries are dropped, and only the first of any
 * repeated id is kept.
 */
export function resolveStageViews(option?: StageViewsOption): StageViewPreset[] {
  let list: readonly StageViewPreset[];
  if (option === undefined) list = DEFAULT_STAGE_VIEWS;
  else if (typeof option === 'function') list = option(DEFAULT_STAGE_VIEWS);
  else {
    const merged = [...DEFAULT_STAGE_VIEWS];
    for (const view of option) {
      const index = merged.findIndex((existing) => existing.id === view?.id);
      if (index >= 0) merged[index] = view;
      else merged.push(view);
    }
    list = merged;
  }
  const seen = new Set<string>();
  const out: StageViewPreset[] = [];
  for (const view of Array.isArray(list) ? list : []) {
    if (!isValidStageView(view) || seen.has(view.id)) continue;
    seen.add(view.id);
    out.push(view);
  }
  return out;
}

/**
 * Camera framing for `view` of a character with `bounds`. The turntable
 * sweep is left out: the studio stops the turntable when a view is chosen.
 */
export function fitStageView(
  bounds: StageBounds,
  view: StageViewPreset,
  options: { fov: number; aspect: number },
): StageCameraFit {
  const { min, max } = bounds;
  let region: StageBounds = bounds;
  if (view.region) {
    const lo = view.region.min;
    const hi = view.region.max;
    const at = (axis: 0 | 1 | 2, t: number) => min[axis] + (max[axis] - min[axis]) * Math.min(Math.max(t, 0), 1);
    const a: StageVec3 = [at(0, lo[0]), at(1, lo[1]), at(2, lo[2])];
    const b: StageVec3 = [at(0, hi[0]), at(1, hi[1]), at(2, hi[2])];
    region = {
      min: [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])],
      max: [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])],
    };
  }
  return fitStageCamera(region, {
    fov: options.fov,
    aspect: options.aspect,
    direction: view.direction,
    margin: view.margin,
    sweep: false,
  });
}
